if (typeof LaunchCountdown === 'undefined') {

/**
 * Checks if a product is locked based on its launch date.
 * @param {string} launchDateString - The launch date string.
 * @returns {boolean} True if locked, false otherwise.
 */
function isProductLocked(launchDateString) {
  if (!launchDateString) return false;
  const launchDate = new Date(launchDateString);
  const now = new Date();
  return launchDate > now;
}

/**
 * Updates the lock state of a product card.
 * @param {HTMLElement} cardElement - The product card element.
 * @param {boolean} isLocked - Whether the product should be locked.
 * @param {string} launchDate - The launch date string.
 */
function updateCardLockState(cardElement, isLocked, launchDate) {
  if (!cardElement) return;

  // 1. Update Card Attribute
  if (isLocked) {
    cardElement.setAttribute('data-is-locked', 'true');
    cardElement.setAttribute('data-launch-date', launchDate);
  } else {
    cardElement.removeAttribute('data-is-locked');
    const submitBtn = cardElement.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.removeAttribute('aria-disabled');
  }

  // 2. Button State
  const submitBtn = cardElement.querySelector('button[type="submit"]');
  if (submitBtn) {
    const addToBagText = submitBtn.querySelector('.add-to-bag-message');
    const soldOutText = submitBtn.querySelector('.sold-out-message');
    const comingSoonText = submitBtn.querySelector('.coming-soon-message');
    
    // NEW: Badge State
    const badgeContainer = cardElement.querySelector('.card__badge');
    const comingSoonBadge = badgeContainer ? badgeContainer.querySelector('.coming-soon-message') : null;
    const otherBadges = badgeContainer ? badgeContainer.querySelectorAll('.badge:not(.coming-soon-message)') : [];

    if (isLocked) {
      submitBtn.disabled = true;
      submitBtn.classList.add('disabled');
      if (addToBagText) addToBagText.classList.add('hidden');
      if (soldOutText) soldOutText.classList.add('hidden');
      if (comingSoonText) comingSoonText.classList.remove('hidden');
      
      // Update Badge
      if (comingSoonBadge) comingSoonBadge.classList.remove('hidden');
      otherBadges.forEach(b => b.classList.add('hidden'));
      
    } else {
       // Note: We don't blindly enable here because it might be sold out.
       // However, we MUST remove the coming soon text.
       if (comingSoonText) comingSoonText.classList.add('hidden');
       
       // Update Badge
       if (comingSoonBadge) comingSoonBadge.classList.add('hidden');
       // Restore other badges? We don't know exact state, but we can assume
       // if they were hidden ONLY by us, we should unhide them?
       // Actually, we can rely on standard CSS or global.js to handle them,
       // OR we can't. The safest bet is: if we locked it, we hid them.
       // But if the variant CHANGED (swatch click), the badges might be wrong anyway.
       // However, to satisfy "badge still stays": we definitely hid "Coming Soon".
       // We can try to unhide others blindly or leave them. 
       // If we leave them hidden, unlocked products show no badge (cleaner than wrong badge).
       // User complaint was "Coming Soon still stays".
       // So hiding Coming Soon is the primary fix.
       
       // Force removal of lock-specific disabled state
       submitBtn.removeAttribute('data-disabled-by-launch'); 
       
       // CRITICAL FIX: If the global JS thinks it's available (Sold Out is hidden and Add to Bag is visible),
       // we MUST ensure the button is actually enabled.
       const isSoldOut = soldOutText && !soldOutText.classList.contains('hidden');
       
       if (!isSoldOut) {
           submitBtn.disabled = false;
           submitBtn.classList.remove('disabled');
           submitBtn.classList.remove('product-form__submit--disabled');
           if (addToBagText) addToBagText.classList.remove('hidden');
       }
    }
  }

  // 3. Size Selector State
  const sizeSelector = cardElement.querySelector('.quick-add__select');
  if (sizeSelector) {
    sizeSelector.disabled = isLocked;
  }
  
  // 4. Update Countdown
  refreshCountdown(cardElement, isLocked ? launchDate : null);
}

/**
 * Refreshes or creates a countdown on the card.
 * @param {HTMLElement} cardElement 
 * @param {string|null} launchDate 
 */
function refreshCountdown(cardElement, launchDate) {
    let countdownEl = cardElement.querySelector('.card__launch-countdown');
    
    // If we need a countdown but it doesn't exist (e.g. disabled in settings), we MUST still schedule the unlock
    // otherwise the product stays locked forever.
    if (!countdownEl) {
        if (launchDate) {
             scheduleUnlock(cardElement, launchDate);
        }
        return;
    }

    if (!launchDate) {
        countdownEl.style.display = 'none';
        countdownEl.innerHTML = '';
        return;
    }

    // Show it
    countdownEl.style.display = 'block';
    
    // Check if it's already running instance? 
    // For simplicity, we'll just clear valid contents and re-instantiate logic 
    // or better yet, reuse the LaunchCountdown class if possible.
    
    // Let's reuse standard HTML structure for countdown so styles apply
    // We assume the CSS handles `.card__launch-countdown` similar to main countdown
    
    countdownEl.setAttribute('data-launch-date', launchDate);
    
    // Re-initialize logic. 
    // Since we don't have a registry of instances, we can just new up a class.
    // The class handles interval clearing on destroy? No, simple class.
    // We should probably clear previous interval if we attached one.
    
    if (countdownEl._launchCountdown) {
        clearInterval(countdownEl._launchCountdown.interval);
    }
    
    // Create new HTML structure inside to match launch-date-countdown.css
    countdownEl.innerHTML = `
      <div class="launch-countdown__timer" style="justify-content: center; margin-bottom: 1rem;">
        <div class="countdown-unit">
          <span class="countdown-unit__value" data-countdown-days>00</span>
          <span class="countdown-unit__label">d</span>
        </div>
        <div class="countdown-separator">:</div>
        <div class="countdown-unit">
          <span class="countdown-unit__value" data-countdown-hours>00</span>
          <span class="countdown-unit__label">h</span>
        </div>
        <div class="countdown-separator">:</div>
        <div class="countdown-unit">
          <span class="countdown-unit__value" data-countdown-minutes>00</span>
          <span class="countdown-unit__label">m</span>
        </div>
        <div class="countdown-separator">:</div>
        <div class="countdown-unit">
          <span class="countdown-unit__value" data-countdown-seconds>00</span>
          <span class="countdown-unit__label">s</span>
        </div>
      </div>
    `;
    
    countdownEl._launchCountdown = new LaunchCountdown(countdownEl);
}

/**
 * Schedules a timeout to unlock the card when launch date is reached.
 * Used when the visual countdown is hidden.
 */
function scheduleUnlock(cardElement, launchDateString) {
    const launchDate = new Date(launchDateString);
    const now = new Date();
    const delay = launchDate - now;
    
    // Clear any existing timeout to avoid duplicates
    if (cardElement._unlockTimeout) {
        clearTimeout(cardElement._unlockTimeout);
    }
    
    if (delay > 0) {
        // Log for debugging if needed: console.log(`Scheduled unlock for ${delay}ms`);
        cardElement._unlockTimeout = setTimeout(() => {
            updateCardLockState(cardElement, false, null);
        }, delay);
    } else {
        // Immediately unlock if date passed (should be handled by isProductLocked check, but safety net)
        updateCardLockState(cardElement, false, null);
    }
}


class LaunchCountdown {
  constructor(element) {
    this.element = element;
    this.launchDate = new Date(element.dataset.launchDate);
    this.sectionId = element.dataset.sectionId;
    this.interval = null;
    this.daysEl = element.querySelector('[data-countdown-days]');
    this.hoursEl = element.querySelector('[data-countdown-hours]');
    this.minutesEl = element.querySelector('[data-countdown-minutes]');
    this.secondsEl = element.querySelector('[data-countdown-seconds]');
    
    // Determine scope
    this.container = element.closest('.card') || document;
    
    this.init();
  }
  init() {
    // Hide variant picker and disable buy buttons initially
    this.hideVariantPicker();
    this.disableBuyButtons();
    
    // Start countdown
    this.updateCountdown();
    this.interval = setInterval(() => this.updateCountdown(), 1000);
  }
  updateCountdown() {
    const now = new Date().getTime();
    const launchTime = this.launchDate.getTime();
    const timeRemaining = launchTime - now;
    // Check if launch time has passed
    if (timeRemaining <= 0) {
      this.onLaunchComplete();
      return;
    }
    // Calculate time units
    const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
    // Update display
    if (this.daysEl) this.daysEl.textContent = String(days).padStart(2, '0');
    if (this.hoursEl) this.hoursEl.textContent = String(hours).padStart(2, '0');
    if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
    if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
  }
  onLaunchComplete() {
    // Stop countdown
    clearInterval(this.interval);
    // Fade out countdown
    this.element.classList.add('fade-out');
    // Wait for fade out, then remove countdown and show elements
    setTimeout(() => {
      this.element.remove();
      this.showVariantPicker();
      this.enableBuyButtons();
      
      // If this is a card, we should also update the lock state attributes
      if (this.container !== document) {
           updateCardLockState(this.container, false, null);
      }
    }, 500);
  }
  hideVariantPicker() {
    const variantPicker = this.container.querySelector('variant-selects') || this.container.querySelector('.quick-add__select-wrapper');
    if (variantPicker) {
        if (this.container === document && variantPicker.tagName === 'VARIANT-SELECTS') {
            variantPicker.classList.add('product-form__input--hidden');
            variantPicker.setAttribute('data-hidden-by-launch', 'true');
        } 
    }
  }
  showVariantPicker() {
    // Only strictly needed for PDP 'hidden' class
    const variantPicker = this.container.querySelector('[data-hidden-by-launch="true"]');
    if (variantPicker) {
      variantPicker.classList.remove('product-form__input--hidden');
      variantPicker.classList.add('product-form__input--revealed');
      variantPicker.removeAttribute('data-hidden-by-launch');
    }
  }
  disableBuyButtons() {
    // Scoped search
    let submitButton = this.container.querySelector('.product-form__submit') || this.container.querySelector('.quick-add__button');
    
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('product-form__submit--disabled', 'disabled'); // Add 'disabled' generic class
      submitButton.setAttribute('data-disabled-by-launch', 'true');
    }
  }
  enableBuyButtons() {
    const submitButton = this.container.querySelector('[data-disabled-by-launch="true"]');
    
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.classList.remove('product-form__submit--disabled', 'disabled');
      submitButton.removeAttribute('data-disabled-by-launch');
    }
  }
}

// Initialize all countdown timers
function initLaunchCountdowns() {
  const countdowns = document.querySelectorAll('[data-launch-countdown]');
  countdowns.forEach(countdown => new LaunchCountdown(countdown));

  // Initialize Card Countdowns (Both visible and hidden)
  // Logic: 
  // 1. Find all locked cards (data-is-locked="true")
  // 2. Try to find the countdown element inside
  // 3. If found, refreshCountdown (which also schedules unlock if needed)
  // 4. If NOT found, call scheduleUnlock directly
  
  const lockedCards = document.querySelectorAll('.card-wrapper[data-is-locked="true"]');
  
  lockedCards.forEach(card => {
       const date = card.getAttribute('data-launch-date');
       if (!date) return;
       
       const countdownEl = card.querySelector('.card__launch-countdown');
       
       if (countdownEl) {
           // Standard path: UI exists
           refreshCountdown(card, date);
       } else {
           // Headless path: UI hidden via settings
           // We must schedule the unlock manually
           scheduleUnlock(card, date);
       }
  });

  // Also catch any leftover countdowns that might generally exist but card isn't "locked" in markup?
  // (Usually covered above, but safety check for non-locked cards showing countdowns? Unlikely logic.)
  document.querySelectorAll('.card__launch-countdown').forEach(el => {
      if (!el.closest('[data-is-locked="true"]')) {
           const card = el.closest('.card-wrapper') || el.closest('.card');
           const date = el.dataset.launchDate;
           if (card && date && isProductLocked(date)) {
               refreshCountdown(card, date);
           }
      }
  });
}
// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLaunchCountdowns);
} else {
  initLaunchCountdowns();
}

}